import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Package,
  Search,
  ChevronDown,
  ChevronUp,
  Rocket,
  AlertCircle,
  RefreshCw,
  X,
} from 'lucide-react'
import { useProducts, useSizeConfigs } from '@/hooks/useProducts'
import { PageHeader } from '@/components/common/PageHeader'
import { SizeBadge } from '@/components/common/SizeBadge'
import { Button } from '@/components/common/Button'
import { Card, CardContent } from '@/components/common/Card'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { EmptyState } from '@/components/common/EmptyState'
import { formatFamily, formatCurrency } from '@/utils/format'
import type { Product, ProductFamily, TShirtSize } from '@/types'
import styles from './ProductsPage.module.css'

const SIZES: TShirtSize[] = ['S', 'M', 'L', 'XL']

export function ProductsPage() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [selectedFamily, setSelectedFamily] = useState<ProductFamily | 'all'>('all')
  const [expandedProduct, setExpandedProduct] = useState<number | null>(null)

  const { data: products, isLoading, isError, refetch } = useProducts()
  const { data: sizeConfigs } = useSizeConfigs()

  const families = useMemo<Array<{ value: ProductFamily | 'all'; label: string }>>(
    () => [
      { value: 'all', label: 'All Families' },
      ...Array.from(new Set((products ?? []).map((p) => p.family))).map((f) => ({
        value: f,
        label: formatFamily(f),
      })),
    ],
    [products]
  )

  const filtered = useMemo(() => {
    return (products ?? []).filter((p) => {
      const matchesSearch =
        !search ||
        p.name.toLowerCase().includes(search.toLowerCase()) ||
        (p.description?.toLowerCase().includes(search.toLowerCase()) ?? false)
      const matchesFamily = selectedFamily === 'all' || p.family === selectedFamily
      return matchesSearch && matchesFamily
    })
  }, [products, search, selectedFamily])

  const grouped = useMemo(() => {
    const groups: Record<string, Product[]> = {}
    filtered.forEach((p) => {
      const family = formatFamily(p.family)
      if (!groups[family]) groups[family] = []
      groups[family].push(p)
    })
    return groups
  }, [filtered])

  const getSizeConfig = (productId: number, size: TShirtSize) =>
    sizeConfigs?.find((sc) => sc.product === productId && sc.size === size)

  if (isError) {
    return (
      <EmptyState
        icon={<AlertCircle size={48} />}
        title="Failed to load products"
        description="Could not fetch the product catalog."
        action={
          <Button onClick={() => refetch()} icon={<RefreshCw size={14} />}>
            Retry
          </Button>
        }
      />
    )
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title="Products"
        subtitle="Browse available Aptean ERP products and size configurations"
      />

      <div className={styles.controls}>
        <div className={styles.searchWrapper}>
          <Search size={15} className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search products..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={styles.searchInput}
          />
          {search && (
            <button className={styles.clearBtn} onClick={() => setSearch('')}>
              <X size={14} />
            </button>
          )}
        </div>

        <div className={styles.familyChips}>
          {families.map((f) => (
            <button
              key={f.value}
              className={`${styles.chip} ${selectedFamily === f.value ? styles.chipActive : ''}`}
              onClick={() => setSelectedFamily(f.value as ProductFamily | 'all')}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <LoadingSpinner label="Loading products..." size="lg" />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<Package size={40} />}
          title="No products found"
          description={
            search ? `No products match "${search}".` : 'No products are configured.'
          }
          action={
            search ? (
              <Button variant="secondary" onClick={() => setSearch('')}>
                Clear Search
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className={styles.familyGroups}>
          {Object.entries(grouped).map(([family, familyProducts]) => (
            <div key={family} className={styles.familyGroup}>
              <h2 className={styles.familyTitle}>{family}</h2>
              <div className={styles.productGrid}>
                {familyProducts.map((product) => {
                  const isExpanded = expandedProduct === product.id
                  return (
                    <div key={product.id} className={styles.productWrapper}>
                      <Card
                        className={`${styles.productCard} ${isExpanded ? styles.productCardExpanded : ''}`}
                      >
                        <CardContent className={styles.productCardContent}>
                          <div className={styles.productHeader}>
                            <div className={styles.productIcon}>
                              <Package size={22} />
                            </div>
                            <div className={styles.productInfo}>
                              <div className={styles.productNameRow}>
                                <span className={styles.productName}>{product.name}</span>
                                {!product.is_active && (
                                  <span className={styles.inactiveBadge}>Inactive</span>
                                )}
                              </div>
                              <span className={styles.productVersion}>v{product.version}</span>
                            </div>
                            <button
                              className={styles.expandBtn}
                              onClick={() =>
                                setExpandedProduct(isExpanded ? null : product.id)
                              }
                            >
                              {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                            </button>
                          </div>

                          <p className={styles.productDesc}>{product.description}</p>

                          <div className={styles.productMeta}>
                            <div className={styles.sizesRow}>
                              <span className={styles.metaLabel}>Sizes:</span>
                              {(product.available_sizes ?? []).map((s) => (
                                <SizeBadge key={s} size={s} />
                              ))}
                            </div>
                            {product.helm_chart && (
                              <span className={styles.helmChart}>{product.helm_chart}</span>
                            )}
                          </div>

                          {isExpanded && (
                            <div className={styles.expandedContent}>
                              <h4 className={styles.tableSectionTitle}>Size Comparison</h4>
                              <div className={styles.sizeTableWrapper}>
                                <table className={styles.sizeTable}>
                                  <thead>
                                    <tr>
                                      <th>Spec</th>
                                      {SIZES.map((s) => (
                                        <th key={s}>
                                          <SizeBadge size={s} />
                                        </th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <SizeTableRow
                                      label="CPU Request"
                                      sizes={SIZES}
                                      getValue={(s) =>
                                        getSizeConfig(product.id, s)?.resources.cpu_request ?? '—'
                                      }
                                    />
                                    <SizeTableRow
                                      label="CPU Limit"
                                      sizes={SIZES}
                                      getValue={(s) =>
                                        getSizeConfig(product.id, s)?.resources.cpu_limit ?? '—'
                                      }
                                    />
                                    <SizeTableRow
                                      label="Memory"
                                      sizes={SIZES}
                                      getValue={(s) =>
                                        getSizeConfig(product.id, s)?.resources.memory_request ?? '—'
                                      }
                                    />
                                    <SizeTableRow
                                      label="Storage"
                                      sizes={SIZES}
                                      getValue={(s) => {
                                        const gb = getSizeConfig(product.id, s)?.resources.storage_gb
                                        return gb !== undefined ? `${gb} GB` : '—'
                                      }}
                                    />
                                    <SizeTableRow
                                      label="DB Tier"
                                      sizes={SIZES}
                                      getValue={(s) =>
                                        getSizeConfig(product.id, s)?.resources.db_tier ?? '—'
                                      }
                                    />
                                    <SizeTableRow
                                      label="Min Replicas"
                                      sizes={SIZES}
                                      getValue={(s) =>
                                        String(
                                          getSizeConfig(product.id, s)?.resources.min_replicas ?? '—'
                                        )
                                      }
                                    />
                                    <SizeTableRow
                                      label="Max Replicas"
                                      sizes={SIZES}
                                      getValue={(s) =>
                                        String(
                                          getSizeConfig(product.id, s)?.resources.max_replicas ?? '—'
                                        )
                                      }
                                    />
                                    <SizeTableRow
                                      label="Est. Cost/mo"
                                      sizes={SIZES}
                                      getValue={(s) => {
                                        const cost =
                                          getSizeConfig(product.id, s)?.resources
                                            .estimated_monthly_cost_usd
                                        return cost !== undefined ? formatCurrency(cost) : '—'
                                      }}
                                      highlight
                                    />
                                  </tbody>
                                </table>
                              </div>
                              <div className={styles.deployAction}>
                                <Button
                                  icon={<Rocket size={14} />}
                                  onClick={() =>
                                    navigate(`/deployments/new?product=${product.id}`)
                                  }
                                >
                                  Deploy {product.name}
                                </Button>
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </div>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function SizeTableRow({
  label,
  sizes,
  getValue,
  highlight,
}: {
  label: string
  sizes: TShirtSize[]
  getValue: (size: TShirtSize) => string
  highlight?: boolean
}) {
  return (
    <tr>
      <td className={styles.sizeRowLabel}>{label}</td>
      {sizes.map((s) => (
        <td
          key={s}
          className={`${styles.sizeRowValue} ${highlight ? styles.sizeRowHighlight : ''}`}
        >
          {getValue(s)}
        </td>
      ))}
    </tr>
  )
}
